import { configureStore, Middleware } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import logger from 'redux-logger';

import userReducer from './slices/userSlice';
import authReducer from './slices/authSlice';
import dashboardReducer from './features/Dashboard/reducers/dashboardSlice';
import taxonomyReducer from './slices/allTaxonomy/taxonomySlice';
import enterpriseContextReducer from './slices/enterpriseContextSlice/enterpriseContextSlice';
import organisationReducer from './slices/organisationSlice';
import opportunityReducer from './slices/FunctionalDesignSlice/OpportunitySlice';
import opportunitiesReducer from './slices/FunctionalDesignSlice/opportunitiesSlice';
import solutionReducer from './slices/SolutionsSlice/solutionSlice';
import diagramReducer from './slices/diagramSlice';
import solutionFormReducer from './slices/solutionFormSlice';
import useCaseReducer from './slices/useCasesSlice';
import aiOpportunitiesReducer from './slices/SolutionsSlice/AiOpportunitiesSlice';
import diagramViewerReducer from './slices/diagramViewerSlice';
import copilorFilterReducer from './slices/CopilotSlice/copilotSlice';
import zbrainReducer from './slices/FunctionalDesignSlice/zbrainSlice';
import solutionLoadingAnimationReducer from './slices/solutionLoadingAnimationSlice';
import solutionXPLReducer from './slices/solutionXPLRSlice';
import versionHistoryReducer from './features/NewSolutionXPLR/OpportunityReport/Versioning/VersionSlice';
import ideationFormReducer from './features/IdeationXPLR/ideationFormSlice';
import newIdeationReducer from './slices/ideationSlice/newIdeationSlice';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web

/* ------------------------------------------------------------------ */
/* Root reducer */
/* ------------------------------------------------------------------ */
const rootReducer = combineReducers({
  user: userReducer,
  auth: authReducer,
  dashboard: dashboardReducer,
  allTaxonomies: taxonomyReducer,
  enterpriseContext: enterpriseContextReducer,
  organisation: organisationReducer,
  opportunity: opportunityReducer,
  opportunities: opportunitiesReducer,
  solutions: solutionReducer,
  diagram: diagramReducer,
  solutionForm: solutionFormReducer,
  useCases: useCaseReducer,
  aiOpportunities: aiOpportunitiesReducer,
  diagramViewer: diagramViewerReducer,
  copilotFilters: copilorFilterReducer,
  solutionLoadingAnimation: solutionLoadingAnimationReducer,
  zbrain: zbrainReducer,
  solutionXPLR: solutionXPLReducer,
  versionHistory: versionHistoryReducer,
  ideationForm: ideationFormReducer,
  newIdeation: newIdeationReducer,
});

/* ------------------------------------------------------------------ */
/* Persist configuration */
/* ------------------------------------------------------------------ */
const persistConfig = {
  key: 'root',
  storage,
  blacklist: [
    'organisation',
    'diagramViewer',
    'opportunity',
    'copilotFilters',
    'solutionXPLR',
    'enterpriseContext',
    'newIdeation',
    'dashboard',
  ], // 💡 this slice is excluded from persistence
};

/* ------------------------------------------------------------------ */
/* Persisted reducer */
/* ------------------------------------------------------------------ */
const persistedReducer = persistReducer(persistConfig, rootReducer);

/* ------------------------------------------------------------------ */
/* Middleware */
/* ------------------------------------------------------------------ */
const middlewares: Middleware[] = [];

if (import.meta.env.VITE_ENV_NAME === 'development') {
  middlewares.push(logger);
}

/* ------------------------------------------------------------------ */
/* Store */
/* ------------------------------------------------------------------ */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(middlewares),
  devTools: true,
});

export const persistor = persistStore(store);

/* ------------------------------------------------------------------ */
/* Types */
/* ------------------------------------------------------------------ */
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
